import { useMemo, useCallback, useState, useEffect } from "react"
import { omit, pick } from "lodash"
import moment from "moment"
import { track } from "helpers/analytics"
import { ACTIONS, TRACKING_EVENTS, STATUSES } from "../constants"
import { generateSummaryKey } from "../model"
import { normalizeTimestamp } from "../helpers"
import useLocalStorageSummaries from "./useLocalSummaries"
import useBaseResource from "./useBaseResource"

function getMostRecentSummary(localStorageSummary, generatedSummary) {
  const localStorageTime = normalizeTimestamp(localStorageSummary.timestamp)
  const generatedTime = normalizeTimestamp(generatedSummary.timestamp)

  if (!localStorageTime) {
    return generatedSummary
  }

  if (!generatedTime) {
    return localStorageSummary
  }

  return generatedTime.format("x") >= localStorageTime.format("x") ? generatedSummary : localStorageSummary
}

function getAvailableSummary(localStorageSummary, generatedSummary, localHasContent, generatedHasContent) {
  if (!localHasContent && !generatedHasContent) {
    return null
  }

  if (!localHasContent && generatedHasContent) {
    return generatedSummary
  }

  if (!generatedHasContent && localHasContent) {
    return localStorageSummary
  }
}

function hasSummaryContent(summary) {
  return !!(summary?.summary || summary?.error)
}

export default function useAISummaryState({ account, action, model, resource, startDate, endDate, base, generate, generatedSummaries, extraParameters }) {
  const [summary, setSummary] = useState(null)
  const [timestamp, setTimestamp] = useState(null)
  const [error, setError] = useState(null)
  const [thumb, setThumb] = useState(null)
  const [lastGenerationTime, setLastGenerationTime] = useState(null)

  const { id: modelId } = model ?? {}
  const baseWithName = useBaseResource(account, modelId, base, endDate)

  const effectiveStartDate = useMemo(() => {
    return action === ACTIONS.SIMULATOR_SUMMARY ? endDate : startDate
  }, [action, endDate, startDate])

  const parameters = useMemo(() => {
    if (!resource) {
      return null
    }
    const { alias: resourceIdOrAlias, type: resourceType, updatedAt: resourceUpdatedAt } = resource ?? {}
    return { account, action, modelId, resourceIdOrAlias, resourceType, resourceUpdatedAt, startDate: effectiveStartDate, endDate, base: baseWithName, ...extraParameters }
  }, [account, action, modelId, endDate, baseWithName, effectiveStartDate, extraParameters, resource])

  const summaryKey = useMemo(() => {
    if (!parameters) {
      return null
    }
    return generateSummaryKey(pick(parameters, ["account", "action", "modelId", "startDate", "endDate", "base", "resourceIdOrAlias", "resourceType", "resourceUpdatedAt"]))
  }, [parameters])

  const [summaries, setLocalStorageSummary] = useLocalStorageSummaries()
  const localStorageSummary = useMemo(() => summaryKey ? summaries?.[summaryKey] : null, [summaries, summaryKey])
  const generatedSummary = useMemo(() => summaryKey ? generatedSummaries?.[summaryKey] : null, [generatedSummaries, summaryKey])

  const mostRecentSummary = useMemo(() => {
    const localHasContent = hasSummaryContent(localStorageSummary)
    const generatedHasContent = hasSummaryContent(generatedSummary)

    if (!localHasContent || !generatedHasContent) {
      return getAvailableSummary(localStorageSummary, generatedSummary, localHasContent, generatedHasContent)
    }

    return getMostRecentSummary(localStorageSummary, generatedSummary)
  }, [localStorageSummary, generatedSummary])

  useEffect(() => {
    if (summaryKey) {
      if (mostRecentSummary?.error) {
        setError(mostRecentSummary.error)
        setLocalStorageSummary(summaryKey, { status: STATUSES.ERROR, error: mostRecentSummary.error })
      } else if (mostRecentSummary?.summary && (!lastGenerationTime || normalizeTimestamp(mostRecentSummary.timestamp).format("x") >= lastGenerationTime.format("x"))) {
        setSummary(mostRecentSummary.summary)
        setTimestamp(normalizeTimestamp(mostRecentSummary.timestamp))
        setThumb(mostRecentSummary.feedback)
      } else {
        setSummary(null)
        setTimestamp(null)
        setThumb(null)
      }
    }
  }, [mostRecentSummary, summaryKey, setLocalStorageSummary, setError, lastGenerationTime])

  useEffect(() => {
    if (summaryKey) {
      const newStatus = generatedSummary?.status
      if (newStatus && newStatus !== STATUSES.DONE) {
        setLocalStorageSummary(summaryKey, { status: newStatus })
      } else if (newStatus === STATUSES.DONE && generatedSummary?.summary && generatedSummary?.timestamp) {
        const generated = generatedSummary
        setLocalStorageSummary(summaryKey, {
          summary: generated.summary,
          // Store in localStorage as unix timestamp string
          timestamp: moment.isMoment(generated.timestamp) ? generated.timestamp.format("x") : moment(generated.timestamp).format("x"),
          meta: generated.meta || { resource, model, base: baseWithName, startDate, endDate },
          status: generated.status,
          isGeneratedByAI: action !== ACTIONS.SIMULATOR_SUMMARY,
        })
      }
    }
  }, [generatedSummary, setLocalStorageSummary, summaryKey, action, model, resource, baseWithName, startDate, endDate])

  const generateSummary = useCallback((eventName, overrideAction) => {
    const actionToUse = overrideAction || action
    const { resourceIdOrAlias, resourceType } = parameters ?? {}
    if (!actionToUse || !resourceIdOrAlias || !resourceType) {
      return
    }
    const isSimulatorSummary = actionToUse === ACTIONS.SIMULATOR_SUMMARY
    const hasActionsAndAttributionMethod = !!(parameters?.actions && parameters?.attributionMethod)
    if (isSimulatorSummary && !hasActionsAndAttributionMethod) {
      return
    }
    track(eventName, { action: actionToUse, resource })
    const now = moment()
    setSummary(null)
    setTimestamp(now)
    setError(null)
    setLastGenerationTime(now)
    setLocalStorageSummary(summaryKey, { status: STATUSES.NOT_STARTED })
    const filteredParameters = !isSimulatorSummary ? omit(parameters, ["actions", "attributionMethod", "analyticsOutput"]) : parameters
    generate({ ...filteredParameters, action: actionToUse, timestamp: now })
  }, [generate, action, parameters, resource, setSummary, setTimestamp, setError, setLocalStorageSummary, summaryKey])

  useEffect(() => {
    if (!summary && !generatedSummary && !localStorageSummary) {
      generateSummary(TRACKING_EVENTS.GENERATE)
    }
  }, [generateSummary, summary, localStorageSummary, generatedSummary])

  return { generateSummary, summaryKey, summary, timestamp, error, setSummary, setTimestamp, setError, thumb, setThumb }
}
